import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/card/card';
import { Button } from '@/components/button/Buttons';
import { KeenIcon } from '@/components';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { useSnackbar } from 'notistack';
import { IAllowedPermissionItem } from '@/services/permissions/permissionsService';

const SubscriptionPermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allowedPermissions, setAllowedPermissions] = useState<
    IAllowedPermissionItem[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const fetchPermissionsForSubscription = async () => {
      if (!subscriptionId) {
        enqueueSnackbar('Invalid subscription ID', { variant: 'error' });
        navigate('/subscription-management');
        return;
      }

      try {
        setLoading(true);

        // Fetch all subscription capabilities with their selected state (include field)
        const allCapabilities =
          await SubscriptionsService.getSubscriptionAllCapabilities(
            subscriptionId
          );

        // Build display list from all capabilities and enrich with module info and checked state
        const matchedPermissions: IAllowedPermissionItem[] = [];
        const selectedIdsSet = new Set<string>();

        (allCapabilities || []).forEach((cap) => {
          // Infer module from unique_key (e.g., "harbor.account.admin_roles_basic" -> "Harbor Account")
          let module: { id: string; name: string } | undefined;
          const parts = cap.unique_key.split('.');
          if (parts.length >= 2) {
            const isHarborReportsPermission =
              cap.unique_key === 'harbor.bookings.reports_basic';
            const isHarborTourismPermission =
              cap.unique_key === 'harbor.discovery.manage_tourism_highlights';
            const moduleKey = isHarborReportsPermission
              ? 'harbor.reports'
              : isHarborTourismPermission
                ? 'harbor.tourism'
                : parts[0] + '.' + parts[1];
            const moduleName = isHarborReportsPermission
              ? 'Harbor Reports'
              : isHarborTourismPermission
                ? 'Harbor Tourism'
                : parts[0].charAt(0).toUpperCase() +
                  parts[0].slice(1) +
                  ' ' +
                  parts[1].charAt(0).toUpperCase() +
                  parts[1].slice(1);
            module = {
              id: moduleKey,
              name: moduleName,
            };
          } else {
            module = {
              id: 'unknown',
              name: 'Unknown Module',
            };
          }

          matchedPermissions.push({
            id: cap.id,
            name: cap.name,
            unique_key: cap.unique_key,
            module,
          });

          // Check if this capability is selected (include: true)
          if (cap.include) {
            selectedIdsSet.add(cap.id);
          }
        });

        setAllowedPermissions(matchedPermissions);
        setSelectedIds(selectedIdsSet);

        // Initialize expanded state based on modules present
        const initialExpanded: Record<string, boolean> = {};
        matchedPermissions.forEach((perm) => {
          const moduleId = perm.module?.id || perm.module?.name || 'unknown';
          if (!(moduleId in initialExpanded)) initialExpanded[moduleId] = false;
        });
        setExpandedModules(initialExpanded);
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load permissions';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setAllowedPermissions([]);
        setSelectedIds(new Set());
      } finally {
        setLoading(false);
      }
    };
    fetchPermissionsForSubscription();
  }, [subscriptionId, enqueueSnackbar, navigate]);

  // Group allowed permissions by module and filter by search term
  type ModuleGroup = {
    module: { id: string; name: string };
    permissions: IAllowedPermissionItem[];
  };
  const filteredModuleGroups = useMemo(() => {
    const grouped = new Map<string, ModuleGroup>();
    const term = searchTerm.toLowerCase();

    allowedPermissions.forEach((perm) => {
      const moduleId = perm.module?.id || perm.module?.name || 'unknown';
      const moduleName = perm.module?.name || 'Unknown Module';
      if (!grouped.has(moduleId))
        grouped.set(moduleId, {
          module: { id: String(moduleId), name: moduleName },
          permissions: [],
        });
      grouped.get(moduleId)!.permissions.push(perm);
    });

    let groups = Array.from(grouped.values());
    if (term.trim()) {
      groups = groups
        .map((g) => {
          const moduleMatch = g.module.name.toLowerCase().includes(term);
          const perms = g.permissions.filter(
            (p) =>
              p.name.toLowerCase().includes(term) ||
              p.unique_key.toLowerCase().includes(term)
          );
          if (!moduleMatch && perms.length === 0) return null;
          return {
            module: g.module,
            permissions: moduleMatch ? g.permissions : perms,
          } as ModuleGroup;
        })
        .filter(Boolean) as ModuleGroup[];
    }

    return groups;
  }, [allowedPermissions, searchTerm]);

  // Check if club.operations.communication_hub is selected
  const isCommunicationHubSelected = useMemo(() => {
    const communicationHubPermission = allowedPermissions.find(
      (p) => p.unique_key === 'club.operations.communication_hub'
    );
    return communicationHubPermission
      ? selectedIds.has(communicationHubPermission.id)
      : false;
  }, [allowedPermissions, selectedIds]);

  // Check if boater discovery maps have at least one selection
  const isBoaterDiscoveryValid = useMemo(() => {
    const mapPermissionKeys = [
      'boater.discovery.basic_map',
      'boater.discovery.estonia_s57_map',
      'boater.discovery.finland_raster_map',
      'boater.discovery.finland_s57_map',
    ];
    const presentMapPermissions = allowedPermissions.filter((p) =>
      mapPermissionKeys.includes(p.unique_key)
    );

    // If none of these map permissions are loaded/available, validation passes (e.g. not a boater subscription)
    if (presentMapPermissions.length === 0) return true;

    // Otherwise, at least one must be checked in selectedIds
    return presentMapPermissions.some((p) => selectedIds.has(p.id));
  }, [allowedPermissions, selectedIds]);

  // Check if a permission should be disabled
  const isPermissionDisabled = (
    permission: IAllowedPermissionItem
  ): boolean => {
    // Disable club.community.* permissions when communication_hub is selected
    if (
      isCommunicationHubSelected &&
      permission.unique_key.startsWith('club.community.')
    ) {
      return true;
    }
    return false;
  };

  // Check if a module's "Select All" checkbox should be disabled
  const isModuleSelectAllDisabled = (group: ModuleGroup): boolean => {
    // Disable "Select All" for club.community module when communication_hub is selected
    if (isCommunicationHubSelected && group.module.id === 'club.community') {
      return true;
    }
    return false;
  };

  const handleGoBack = () => navigate('/subscription-management');

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const areAllPermissionsSelected = (group: ModuleGroup): boolean => {
    return (
      group.permissions.length > 0 &&
      group.permissions.every((perm) => selectedIds.has(perm.id))
    );
  };

  const handlePermissionChange = (permissionId: string, isChecked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isChecked) next.add(permissionId);
      else next.delete(permissionId);

      // Find the permission to get its unique_key
      const changedPermission = allowedPermissions.find(
        (p) => p.id === permissionId
      );

      if (changedPermission) {
        const uniqueKey = changedPermission.unique_key;

        // If harbor.discovery.listed_in_map_and_search is toggled, toggle harbor.navigation.routing_target_for_boaters
        if (uniqueKey === 'harbor.discovery.listed_in_map_and_search') {
          const routingTargetPermission = allowedPermissions.find(
            (p) =>
              p.unique_key === 'harbor.navigation.routing_target_for_boaters'
          );
          if (routingTargetPermission) {
            if (isChecked) {
              next.add(routingTargetPermission.id);
            } else {
              next.delete(routingTargetPermission.id);
            }
          }
        }

        // If harbor.navigation.routing_target_for_boaters is toggled, toggle harbor.discovery.listed_in_map_and_search
        if (uniqueKey === 'harbor.navigation.routing_target_for_boaters') {
          const discoveryPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.discovery.listed_in_map_and_search'
          );
          if (discoveryPermission) {
            if (isChecked) {
              next.add(discoveryPermission.id);
            } else {
              next.delete(discoveryPermission.id);
            }
          }
        }

        // If harbor.bookings.manage_bookables is toggled, toggle harbor.bookings.manage_booking_requests
        if (uniqueKey === 'harbor.bookings.manage_bookables') {
          const manageBookingRequestsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_booking_requests'
          );
          if (manageBookingRequestsPermission) {
            if (isChecked) {
              next.add(manageBookingRequestsPermission.id);
            } else {
              next.delete(manageBookingRequestsPermission.id);
            }
          }
        }

        // If harbor.bookings.manage_booking_requests is toggled, toggle harbor.bookings.manage_bookables
        if (uniqueKey === 'harbor.bookings.manage_booking_requests') {
          const manageBookablesPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_bookables'
          );
          if (manageBookablesPermission) {
            if (isChecked) {
              next.add(manageBookablesPermission.id);
            } else {
              next.delete(manageBookablesPermission.id);
            }
          }
        }

        // If harbor.account.financial_statements_basic is toggled, toggle harbor.bookings.reports_basic
        if (uniqueKey === 'harbor.account.financial_statements_basic') {
          const reportsBasicPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.reports_basic'
          );
          if (reportsBasicPermission) {
            if (isChecked) {
              next.add(reportsBasicPermission.id);
            } else {
              next.delete(reportsBasicPermission.id);
            }
          }
        }

        // If harbor.bookings.reports_basic is toggled, toggle harbor.account.financial_statements_basic
        if (uniqueKey === 'harbor.bookings.reports_basic') {
          const financialStatementsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.account.financial_statements_basic'
          );
          if (financialStatementsPermission) {
            if (isChecked) {
              next.add(financialStatementsPermission.id);
            } else {
              next.delete(financialStatementsPermission.id);
            }
          }
        }

        // If club.discovery.harbor_list_basic is toggled, toggle club.operations.harbor_berth_stack
        if (uniqueKey === 'club.discovery.harbor_list_basic') {
          const harborBerthStackPermission = allowedPermissions.find(
            (p) => p.unique_key === 'club.operations.harbor_berth_stack'
          );
          if (harborBerthStackPermission) {
            if (isChecked) {
              next.add(harborBerthStackPermission.id);
            } else {
              next.delete(harborBerthStackPermission.id);
            }
          }
        }

        // If club.operations.harbor_berth_stack is toggled, toggle club.discovery.harbor_list_basic
        if (uniqueKey === 'club.operations.harbor_berth_stack') {
          const harborListBasicPermission = allowedPermissions.find(
            (p) => p.unique_key === 'club.discovery.harbor_list_basic'
          );
          if (harborListBasicPermission) {
            if (isChecked) {
              next.add(harborListBasicPermission.id);
            } else {
              next.delete(harborListBasicPermission.id);
            }
          }
        }

        // If club.operations.communication_hub is toggled, select all club.community.* permissions
        if (uniqueKey === 'club.operations.communication_hub') {
          const communityPermissions = allowedPermissions.filter((p) =>
            p.unique_key.startsWith('club.community.')
          );
          communityPermissions.forEach((perm) => {
            if (isChecked) {
              next.add(perm.id);
            } else {
              next.delete(perm.id);
            }
          });
        }

        // If boater.account.multi_language_ui is toggled, toggle boater.profile.basic_personal_profile
        if (uniqueKey === 'boater.account.multi_language_ui') {
          const basicPersonalProfilePermission = allowedPermissions.find(
            (p) => p.unique_key === 'boater.profile.basic_personal_profile'
          );
          if (basicPersonalProfilePermission) {
            if (isChecked) {
              next.add(basicPersonalProfilePermission.id);
            } else {
              next.delete(basicPersonalProfilePermission.id);
            }
          }
        }

        // If boater.profile.basic_personal_profile is toggled, toggle boater.account.multi_language_ui
        if (uniqueKey === 'boater.profile.basic_personal_profile') {
          const multiLanguageUiPermission = allowedPermissions.find(
            (p) => p.unique_key === 'boater.account.multi_language_ui'
          );
          if (multiLanguageUiPermission) {
            if (isChecked) {
              next.add(multiLanguageUiPermission.id);
            } else {
              next.delete(multiLanguageUiPermission.id);
            }
          }
        }

        // If boater.bookings.manage_bookings_basic is toggled, toggle boater.bookings.access_booking_history_limited
        if (uniqueKey === 'boater.bookings.manage_bookings_basic') {
          const accessBookingHistoryPermission = allowedPermissions.find(
            (p) =>
              p.unique_key === 'boater.bookings.access_booking_history_limited'
          );
          if (accessBookingHistoryPermission) {
            if (isChecked) {
              next.add(accessBookingHistoryPermission.id);
            } else {
              next.delete(accessBookingHistoryPermission.id);
            }
          }
        }

        // If harbor.alerts.standard_alerts is toggled, toggle harbor.bookings.accept_online_bookings, manage_bookables, and manage_booking_requests
        if (uniqueKey === 'harbor.alerts.standard_alerts') {
          const acceptOnlineBookingsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.accept_online_bookings'
          );
          const manageBookablesPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_bookables'
          );
          const manageBookingRequestsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_booking_requests'
          );

          if (acceptOnlineBookingsPermission) {
            if (isChecked) {
              next.add(acceptOnlineBookingsPermission.id);
            } else {
              next.delete(acceptOnlineBookingsPermission.id);
            }
          }
          if (manageBookablesPermission) {
            if (isChecked) {
              next.add(manageBookablesPermission.id);
            } else {
              next.delete(manageBookablesPermission.id);
            }
          }
          if (manageBookingRequestsPermission) {
            if (isChecked) {
              next.add(manageBookingRequestsPermission.id);
            } else {
              next.delete(manageBookingRequestsPermission.id);
            }
          }
        }

        // If harbor.bookings.accept_online_bookings, manage_bookables, or manage_booking_requests is toggled, toggle harbor.alerts.standard_alerts
        if (
          uniqueKey === 'harbor.bookings.accept_online_bookings' ||
          uniqueKey === 'harbor.bookings.manage_bookables' ||
          uniqueKey === 'harbor.bookings.manage_booking_requests'
        ) {
          const acceptOnlineBookingsPerm = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.accept_online_bookings'
          );
          const manageBookablesPerm = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_bookables'
          );
          const manageBookingRequestsPerm = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_booking_requests'
          );

          [
            acceptOnlineBookingsPerm,
            manageBookablesPerm,
            manageBookingRequestsPerm,
          ].forEach((perm) => {
            if (!perm) return;
            if (isChecked) {
              next.add(perm.id);
            } else {
              next.delete(perm.id);
            }
          });

          const standardAlertsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.alerts.standard_alerts'
          );

          if (standardAlertsPermission) {
            if (isChecked) {
              // If any booking permission is checked, check standard_alerts
              next.add(standardAlertsPermission.id);
            } else {
              // If a booking permission is unchecked, check if all three are unchecked
              const allUnchecked =
                (!acceptOnlineBookingsPerm ||
                  !next.has(acceptOnlineBookingsPerm.id)) &&
                (!manageBookablesPerm || !next.has(manageBookablesPerm.id)) &&
                (!manageBookingRequestsPerm ||
                  !next.has(manageBookingRequestsPerm.id));

              if (allUnchecked) {
                next.delete(standardAlertsPermission.id);
              }
            }
          }
        }

        // If harbor.inventory.manage_berths, manage_categories, or manage_pier is toggled, toggle the other two
        if (
          uniqueKey === 'harbor.inventory.manage_berths' ||
          uniqueKey === 'harbor.inventory.manage_categories' ||
          uniqueKey === 'harbor.inventory.manage_pier'
        ) {
          const manageBerthsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.inventory.manage_berths'
          );
          const manageCategoriesPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.inventory.manage_categories'
          );
          const managePierPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.inventory.manage_pier'
          );

          [
            manageBerthsPermission,
            manageCategoriesPermission,
            managePierPermission,
          ].forEach((perm) => {
            if (!perm) return;
            if (isChecked) {
              next.add(perm.id);
            } else {
              next.delete(perm.id);
            }
          });
        }
      }

      return next;
    });
  };

  const handleSelectAllPermissions = (moduleId: string, isChecked: boolean) => {
    // Get all allowed permissions for this module
    const modulePerms = allowedPermissions.filter(
      (p) => (p.module?.id || p.module?.name || 'unknown') === moduleId
    );

    setSelectedIds((prev) => {
      const next = new Set(prev);
      const processedIds = new Set<string>(); // Track processed permissions to avoid double-processing

      modulePerms.forEach((perm) => {
        const id = perm.id;
        const uniqueKey = perm.unique_key;

        // Skip if already processed via dependency
        if (processedIds.has(id)) return;

        if (isChecked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        processedIds.add(id);

        // Handle dependency: harbor.discovery.listed_in_map_and_search <-> harbor.navigation.routing_target_for_boaters
        if (uniqueKey === 'harbor.discovery.listed_in_map_and_search') {
          const routingTargetPermission = allowedPermissions.find(
            (p) =>
              p.unique_key === 'harbor.navigation.routing_target_for_boaters'
          );
          if (
            routingTargetPermission &&
            !processedIds.has(routingTargetPermission.id)
          ) {
            if (isChecked) {
              next.add(routingTargetPermission.id);
            } else {
              next.delete(routingTargetPermission.id);
            }
            processedIds.add(routingTargetPermission.id);
          }
        } else if (
          uniqueKey === 'harbor.navigation.routing_target_for_boaters'
        ) {
          const discoveryPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.discovery.listed_in_map_and_search'
          );
          if (
            discoveryPermission &&
            !processedIds.has(discoveryPermission.id)
          ) {
            if (isChecked) {
              next.add(discoveryPermission.id);
            } else {
              next.delete(discoveryPermission.id);
            }
            processedIds.add(discoveryPermission.id);
          }
        }

        // Handle dependency: harbor.bookings.manage_bookables <-> harbor.bookings.manage_booking_requests
        if (uniqueKey === 'harbor.bookings.manage_bookables') {
          const manageBookingRequestsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_booking_requests'
          );
          if (
            manageBookingRequestsPermission &&
            !processedIds.has(manageBookingRequestsPermission.id)
          ) {
            if (isChecked) {
              next.add(manageBookingRequestsPermission.id);
            } else {
              next.delete(manageBookingRequestsPermission.id);
            }
            processedIds.add(manageBookingRequestsPermission.id);
          }
        } else if (uniqueKey === 'harbor.bookings.manage_booking_requests') {
          const manageBookablesPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_bookables'
          );
          if (
            manageBookablesPermission &&
            !processedIds.has(manageBookablesPermission.id)
          ) {
            if (isChecked) {
              next.add(manageBookablesPermission.id);
            } else {
              next.delete(manageBookablesPermission.id);
            }
            processedIds.add(manageBookablesPermission.id);
          }
        }

        // Handle dependency: harbor.account.financial_statements_basic <-> harbor.bookings.reports_basic
        if (uniqueKey === 'harbor.account.financial_statements_basic') {
          const reportsBasicPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.reports_basic'
          );
          if (
            reportsBasicPermission &&
            !processedIds.has(reportsBasicPermission.id)
          ) {
            if (isChecked) {
              next.add(reportsBasicPermission.id);
            } else {
              next.delete(reportsBasicPermission.id);
            }
            processedIds.add(reportsBasicPermission.id);
          }
        } else if (uniqueKey === 'harbor.bookings.reports_basic') {
          const financialStatementsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.account.financial_statements_basic'
          );
          if (
            financialStatementsPermission &&
            !processedIds.has(financialStatementsPermission.id)
          ) {
            if (isChecked) {
              next.add(financialStatementsPermission.id);
            } else {
              next.delete(financialStatementsPermission.id);
            }
            processedIds.add(financialStatementsPermission.id);
          }
        }

        // Handle dependency: club.discovery.harbor_list_basic <-> club.operations.harbor_berth_stack
        if (uniqueKey === 'club.discovery.harbor_list_basic') {
          const harborBerthStackPermission = allowedPermissions.find(
            (p) => p.unique_key === 'club.operations.harbor_berth_stack'
          );
          if (
            harborBerthStackPermission &&
            !processedIds.has(harborBerthStackPermission.id)
          ) {
            if (isChecked) {
              next.add(harborBerthStackPermission.id);
            } else {
              next.delete(harborBerthStackPermission.id);
            }
            processedIds.add(harborBerthStackPermission.id);
          }
        } else if (uniqueKey === 'club.operations.harbor_berth_stack') {
          const harborListBasicPermission = allowedPermissions.find(
            (p) => p.unique_key === 'club.discovery.harbor_list_basic'
          );
          if (
            harborListBasicPermission &&
            !processedIds.has(harborListBasicPermission.id)
          ) {
            if (isChecked) {
              next.add(harborListBasicPermission.id);
            } else {
              next.delete(harborListBasicPermission.id);
            }
            processedIds.add(harborListBasicPermission.id);
          }
        }

        // Handle dependency: club.operations.communication_hub -> all club.community.* permissions
        if (uniqueKey === 'club.operations.communication_hub') {
          const communityPermissions = allowedPermissions.filter(
            (p) =>
              p.unique_key.startsWith('club.community.') &&
              !processedIds.has(p.id)
          );
          communityPermissions.forEach((perm) => {
            if (isChecked) {
              next.add(perm.id);
            } else {
              next.delete(perm.id);
            }
            processedIds.add(perm.id);
          });
        }

        // Handle dependency: boater.account.multi_language_ui <-> boater.profile.basic_personal_profile
        if (uniqueKey === 'boater.account.multi_language_ui') {
          const basicPersonalProfilePermission = allowedPermissions.find(
            (p) => p.unique_key === 'boater.profile.basic_personal_profile'
          );
          if (
            basicPersonalProfilePermission &&
            !processedIds.has(basicPersonalProfilePermission.id)
          ) {
            if (isChecked) {
              next.add(basicPersonalProfilePermission.id);
            } else {
              next.delete(basicPersonalProfilePermission.id);
            }
            processedIds.add(basicPersonalProfilePermission.id);
          }
        } else if (uniqueKey === 'boater.profile.basic_personal_profile') {
          const multiLanguageUiPermission = allowedPermissions.find(
            (p) => p.unique_key === 'boater.account.multi_language_ui'
          );
          if (
            multiLanguageUiPermission &&
            !processedIds.has(multiLanguageUiPermission.id)
          ) {
            if (isChecked) {
              next.add(multiLanguageUiPermission.id);
            } else {
              next.delete(multiLanguageUiPermission.id);
            }
            processedIds.add(multiLanguageUiPermission.id);
          }
        }

        // Handle dependency: boater.bookings.manage_bookings_basic -> boater.bookings.access_booking_history_limited
        if (uniqueKey === 'boater.bookings.manage_bookings_basic') {
          const accessBookingHistoryPermission = allowedPermissions.find(
            (p) =>
              p.unique_key === 'boater.bookings.access_booking_history_limited'
          );
          if (
            accessBookingHistoryPermission &&
            !processedIds.has(accessBookingHistoryPermission.id)
          ) {
            if (isChecked) {
              next.add(accessBookingHistoryPermission.id);
            } else {
              next.delete(accessBookingHistoryPermission.id);
            }
            processedIds.add(accessBookingHistoryPermission.id);
          }
        }

        // Handle dependency: harbor.alerts.standard_alerts <-> harbor.bookings.accept_online_bookings, manage_bookables, manage_booking_requests
        if (uniqueKey === 'harbor.alerts.standard_alerts') {
          const acceptOnlineBookingsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.accept_online_bookings'
          );
          const manageBookablesPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_bookables'
          );
          const manageBookingRequestsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_booking_requests'
          );

          if (
            acceptOnlineBookingsPermission &&
            !processedIds.has(acceptOnlineBookingsPermission.id)
          ) {
            if (isChecked) {
              next.add(acceptOnlineBookingsPermission.id);
            } else {
              next.delete(acceptOnlineBookingsPermission.id);
            }
            processedIds.add(acceptOnlineBookingsPermission.id);
          }
          if (
            manageBookablesPermission &&
            !processedIds.has(manageBookablesPermission.id)
          ) {
            if (isChecked) {
              next.add(manageBookablesPermission.id);
            } else {
              next.delete(manageBookablesPermission.id);
            }
            processedIds.add(manageBookablesPermission.id);
          }
          if (
            manageBookingRequestsPermission &&
            !processedIds.has(manageBookingRequestsPermission.id)
          ) {
            if (isChecked) {
              next.add(manageBookingRequestsPermission.id);
            } else {
              next.delete(manageBookingRequestsPermission.id);
            }
            processedIds.add(manageBookingRequestsPermission.id);
          }
        } else if (
          uniqueKey === 'harbor.bookings.accept_online_bookings' ||
          uniqueKey === 'harbor.bookings.manage_bookables' ||
          uniqueKey === 'harbor.bookings.manage_booking_requests'
        ) {
          const acceptOnlineBookingsPerm = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.accept_online_bookings'
          );
          const manageBookablesPerm = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_bookables'
          );
          const manageBookingRequestsPerm = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.bookings.manage_booking_requests'
          );

          [
            acceptOnlineBookingsPerm,
            manageBookablesPerm,
            manageBookingRequestsPerm,
          ].forEach((perm) => {
            if (!perm || processedIds.has(perm.id)) return;
            if (isChecked) {
              next.add(perm.id);
            } else {
              next.delete(perm.id);
            }
            processedIds.add(perm.id);
          });

          const standardAlertsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.alerts.standard_alerts'
          );

          if (
            standardAlertsPermission &&
            !processedIds.has(standardAlertsPermission.id)
          ) {
            if (isChecked) {
              // If any booking permission is checked, check standard_alerts
              next.add(standardAlertsPermission.id);
            } else {
              // If a booking permission is unchecked, check if all three are unchecked
              const allUnchecked =
                (!acceptOnlineBookingsPerm ||
                  !next.has(acceptOnlineBookingsPerm.id)) &&
                (!manageBookablesPerm || !next.has(manageBookablesPerm.id)) &&
                (!manageBookingRequestsPerm ||
                  !next.has(manageBookingRequestsPerm.id));

              if (allUnchecked) {
                next.delete(standardAlertsPermission.id);
              }
            }
            processedIds.add(standardAlertsPermission.id);
          }
        } else if (
          uniqueKey === 'harbor.inventory.manage_berths' ||
          uniqueKey === 'harbor.inventory.manage_categories' ||
          uniqueKey === 'harbor.inventory.manage_pier'
        ) {
          const manageBerthsPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.inventory.manage_berths'
          );
          const manageCategoriesPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.inventory.manage_categories'
          );
          const managePierPermission = allowedPermissions.find(
            (p) => p.unique_key === 'harbor.inventory.manage_pier'
          );

          [
            manageBerthsPermission,
            manageCategoriesPermission,
            managePierPermission,
          ].forEach((perm) => {
            if (!perm || processedIds.has(perm.id)) return;
            if (isChecked) {
              next.add(perm.id);
            } else {
              next.delete(perm.id);
            }
            processedIds.add(perm.id);
          });
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!subscriptionId) {
      enqueueSnackbar('Invalid subscription ID', { variant: 'error' });
      return;
    }

    if (!isBoaterDiscoveryValid) {
      enqueueSnackbar(
        'At least one map permission (Basic Map, Estonia S57 Map, Finland Raster Map, or Finland S57 Map) must be selected under Boater Discovery.',
        { variant: 'error' }
      );
      return;
    }

    try {
      setSaving(true);
      // Collect checked permission ids from selection
      // If no permissions are selected, pass null instead of empty array
      const ids: string[] | null =
        selectedIds.size === 0 ? null : Array.from(selectedIds);

      await SubscriptionsService.updateSubscriptionPermissions(
        subscriptionId,
        ids
      );
      enqueueSnackbar('Permissions saved successfully', { variant: 'success' });
      navigate('/subscription-management');
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save permissions';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Edit Subscription Permissions</title>
      </Helmet>

      {loading ? (
        <div className="p-10 flex items-center justify-center">
          <div
            className="text-center"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading permissions...</p>
          </div>
        </div>
      ) : (
        <div className="container mx-auto py-8 px-4">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Edit Subscription Permissions
              </h1>
              <p className="text-gray-600 mt-1">
                Select the permissions you want to grant to this subscription.
              </p>
            </div>
            <div>
              <Button variant="secondary" onClick={handleGoBack}>
                Back to Subscriptions
              </Button>
            </div>
          </div>

          <Card className="overflow-hidden shadow-lg border-0">
            <div className="bg-gray-100 p-6">
              {/* Search */}
              <div
                className="mb-6"
                role="search"
                aria-label="Search permissions"
              >
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center"
                  htmlFor="search-permissions"
                  aria-label="Search permissions"
                >
                  <KeenIcon
                    type="filled"
                    icon="magnifier"
                    className="text-gray-500"
                  />
                  <input
                    id="search-permissions"
                    placeholder="Search permissions..."
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                    aria-label="Search permissions by module or permission name"
                    role="searchbox"
                  />
                </label>
              </div>
              {/* Modules list */}
              <div className="space-y-4">
                {filteredModuleGroups.length === 0 ? (
                  <div className="py-6 px-4 text-gray-500">
                    No permissions found.
                  </div>
                ) : (
                  filteredModuleGroups.map((group) => {
                    const hasPermissions = group.permissions.length > 0;
                    if (!hasPermissions) return null;

                    return (
                      <div
                        key={group.module.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        {/* Accordion header acts as the toggle control */}
                        <div
                          className="bg-gray-200 p-4 flex items-center justify-between cursor-pointer"
                          role="article"
                          tabIndex={0}
                          aria-controls={`module-panel-${group.module.id}`}
                          aria-expanded={Boolean(
                            expandedModules[group.module.id]
                          )}
                          aria-labelledby={`module-label-${group.module.id}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleModuleExpansion(group.module.id);
                            }
                          }}
                          onClick={() => toggleModuleExpansion(group.module.id)}
                        >
                          <div
                            className="flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center">
                              <input
                                id={`module-${group.module.id}`}
                                type="checkbox"
                                className="w-5 h-5 cursor-pointer text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                checked={areAllPermissionsSelected(group)}
                                disabled={isModuleSelectAllDisabled(group)}
                                aria-label={`Select all permissions in ${group.module.name}`}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectAllPermissions(
                                    group.module.id,
                                    e.target.checked
                                  );
                                }}
                              />
                              <label
                                htmlFor={`module-${group.module.id}`}
                                className="ml-3 text-lg font-medium text-gray-900 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                                id={`module-label-${group.module.id}`}
                              >
                                {group.module.name}
                              </label>
                            </div>
                            {group.module.id === 'boater.discovery' &&
                              !isBoaterDiscoveryValid && (
                                <p
                                  className="text-red-500 text-xs mt-1 ml-8 contrast:text-[#FF8B33]"
                                  role="alert"
                                >
                                  At least one map permission (Basic Map,
                                  Estonia S57 Map, Finland Raster Map, or
                                  Finland S57 Map) must be selected.
                                </p>
                              )}
                          </div>

                          <div
                            className="flex items-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="text-gray-500 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleModuleExpansion(group.module.id);
                              }}
                              aria-label={`${expandedModules[group.module.id] ? 'Collapse' : 'Expand'} ${group.module.name} permissions`}
                              aria-expanded={Boolean(
                                expandedModules[group.module.id]
                              )}
                              aria-controls={`module-panel-${group.module.id}`}
                            >
                              {expandedModules[group.module.id] ? (
                                <svg
                                  className="w-5 h-5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-5 h-5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>

                        {expandedModules[group.module.id] && (
                          <div
                            id={`module-panel-${group.module.id}`}
                            role="region"
                            aria-labelledby={`module-label-${group.module.id}`}
                            className="pl-10 divide-y divide-gray-200"
                          >
                            {group.permissions.map((permission) => (
                              <div
                                key={permission.id}
                                className="py-3 px-4 flex items-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div
                                  className="flex items-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    id={`permission-${permission.id}`}
                                    type="checkbox"
                                    className="w-4 h-4 cursor-pointer text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    checked={selectedIds.has(permission.id)}
                                    disabled={isPermissionDisabled(permission)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handlePermissionChange(
                                        permission.id,
                                        e.target.checked
                                      );
                                    }}
                                  />
                                  <label
                                    htmlFor={`permission-${permission.id}`}
                                    className="ml-3 text-base text-gray-700 cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {permission.name}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Card>

          {/* Save/Cancel buttons */}
          <div
            className="flex justify-end mt-6 gap-5"
            role="toolbar"
            aria-label="Permission actions"
          >
            <Button variant="secondary" onClick={handleGoBack}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Permissions'
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionPermissionsPage;
